export function CalgaryRules() {
  const requirements = [
    {
      icon: 'ri-file-list-3-line',
      title: 'Two-Tiered Licensing System',
      description: 'Tier 1 (~$172): Under 2 rooms. Tier 2 (~$510): 3+ rooms or entire homes. Non-primary residence operators pay higher fees.',
      highlight: true
    },
    {
      icon: 'ri-pause-circle-line',
      title: 'Vacancy Rate "Pause Rule"',
      description: 'If Calgary\'s rental vacancy rate drops below 2.5%, the city stops issuing new licenses for non-primary homes to protect long-term housing supply.',
      highlight: true
    },
    {
      icon: 'ri-home-line',
      title: 'No Principal Residence Requirement',
      description: 'Investment properties are allowed, making Calgary investor-friendly. However, non-primary residents pay higher licensing fees.',
      highlight: false
    },
    {
      icon: 'ri-calendar-line',
      title: '365-Night Maximum',
      description: 'No annual night cap restrictions. You can operate your STR year-round without duration limits.',
      highlight: false
    },
    {
      icon: 'ri-shield-check-line',
      title: 'Safety & Compliance',
      description: 'Fire extinguishers, smoke detectors, and emergency exit plans required. Visual floor plan must be submitted with application.',
      highlight: false
    },
    {
      icon: 'ri-advertisement-line',
      title: 'License Number in Ads',
      description: 'All online listings must display your license number. Missing license numbers result in $500+ fines during proactive audits.',
      highlight: false
    }
  ];

  const process = [
    {
      step: '1',
      title: 'Determine Your Tier',
      description: 'Calculate how many rooms you\'ll rent. Under 2 rooms = Tier 1 (~$172). 3+ rooms or entire home = Tier 2 (~$510).'
    },
    {
      step: '2',
      title: 'Gather Documentation',
      description: 'Government ID, proof of ownership or landlord consent, property insurance, and a visual floor plan showing exits and fire safety equipment.'
    },
    {
      step: '3',
      title: 'Submit Online Application',
      description: 'Apply through Calgary\'s online portal. Pay the appropriate tier fee. Non-primary residence operators pay additional fees.'
    },
    {
      step: '4',
      title: 'Safety Inspection',
      description: 'Some properties may require an inspection to verify fire safety compliance, especially for Tier 2 licenses.'
    },
    {
      step: '5',
      title: 'Receive License & Display',
      description: 'Once approved, display your license number on all online listings (Airbnb, Vrbo, etc.) to avoid $500+ fines.'
    }
  ];

  const hotspots = [
    {
      name: 'Beltline',
      description: 'Downtown core with high demand from business travelers and tourists. Close to restaurants, nightlife, and the Calgary Tower.',
      revenue: 'High year-round'
    },
    {
      name: 'East Village',
      description: 'Trendy neighborhood near the Bow River with modern condos. Popular with young professionals and event attendees.',
      revenue: 'Peak during Stampede'
    },
    {
      name: 'Kensington',
      description: 'Charming area with boutique shops and cafes. Appeals to leisure travelers seeking local experiences.',
      revenue: 'Steady demand'
    }
  ];

  return (
    <section className="py-24 lg:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="text-center mb-16 max-w-3xl mx-auto">
          <h2 className="font-['Fraunces'] text-4xl lg:text-5xl font-bold text-[#505A5A] mb-6">
            Calgary STR Regulations 2026
          </h2>
          <p className="text-lg text-[#505A5A]/70 leading-relaxed">
            Calgary offers a flexible, investor-friendly framework with a two-tiered licensing system. No principal residence requirement, but the city monitors vacancy rates to protect long-term housing.
          </p>
        </div>

        {/* Key Requirements */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-20">
          {requirements.map((req, index) => (
            <div 
              key={index}
              className={`p-8 rounded-2xl transition-all ${
                req.highlight 
                  ? 'bg-[#882629] text-white shadow-xl' 
                  : 'bg-[#F0F0F0] hover:shadow-lg'
              }`}
            >
              <div className={`w-16 h-16 flex items-center justify-center rounded-xl mb-6 ${
                req.highlight 
                  ? 'bg-white/20' 
                  : 'bg-[#882629]/10'
              }`}>
                <i className={`${req.icon} text-3xl ${
                  req.highlight ? 'text-white' : 'text-[#882629]'
                }`}></i>
              </div>
              <h3 className={`text-xl font-semibold mb-4 ${
                req.highlight ? 'text-white' : 'text-[#505A5A]'
              }`}>
                {req.title}
              </h3>
              <p className={`leading-relaxed ${
                req.highlight ? 'text-white/90' : 'text-[#505A5A]/70'
              }`}>
                {req.description}
              </p>
            </div>
          ))}
        </div>

        {/* Licensing Process */}
        <div className="mb-20">
          <h3 className="text-3xl font-semibold text-[#505A5A] mb-12 text-center">
            Licensing Process
          </h3>
          <div className="grid md:grid-cols-5 gap-6">
            {process.map((item, index) => (
              <div key={index} className="relative">
                <div className="bg-[#F0F0F0] p-6 rounded-2xl h-full hover:shadow-lg transition-all">
                  <div className="w-12 h-12 flex items-center justify-center bg-[#882629] text-white rounded-full text-xl font-bold mb-4">
                    {item.step}
                  </div>
                  <h4 className="text-lg font-semibold text-[#505A5A] mb-3">
                    {item.title}
                  </h4>
                  <p className="text-sm text-[#505A5A]/70 leading-relaxed">
                    {item.description}
                  </p>
                </div>
                {index < process.length - 1 && (
                  <div className="hidden md:block absolute top-6 -right-3 w-6 h-6 flex items-center justify-center">
                    <i className="ri-arrow-right-line text-2xl text-[#882629]"></i>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Market Hotspots */}
        <div className="bg-[#F0F0F0] rounded-2xl p-8 lg:p-12 mb-12">
          <h3 className="text-3xl font-semibold text-[#505A5A] mb-8 text-center">
            Top Calgary STR Hotspots
          </h3>
          <div className="grid md:grid-cols-3 gap-8">
            {hotspots.map((spot, index) => (
              <div key={index} className="bg-white p-6 rounded-xl">
                <h4 className="text-xl font-semibold text-[#882629] mb-3">
                  {spot.name}
                </h4>
                <p className="text-[#505A5A]/70 mb-4 leading-relaxed">
                  {spot.description}
                </p>
                <div className="flex items-center gap-2 text-sm font-semibold text-[#505A5A]">
                  <i className="ri-line-chart-line text-[#882629]"></i>
                  {spot.revenue}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Calgary Stampede Highlight */}
        <div className="bg-gradient-to-r from-[#882629] to-[#A63234] text-white rounded-2xl p-8 lg:p-12">
          <div className="flex flex-col lg:flex-row items-center gap-8">
            <div className="w-20 h-20 flex items-center justify-center bg-white/20 rounded-full flex-shrink-0">
              <i className="ri-trophy-line text-5xl text-white"></i>
            </div>
            <div className="flex-1 text-center lg:text-left">
              <h3 className="text-2xl font-semibold mb-3">
                Calgary Stampede: Highest Revenue Window in Canada
              </h3>
              <p className="text-white/90 leading-relaxed mb-4">
                The Calgary Stampede (July) generates the highest STR revenue window in the entire country. Properties in Beltline and East Village can command premium rates during this 10-day event.
              </p>
              <div className="flex flex-wrap gap-4 justify-center lg:justify-start">
                <div className="bg-white/20 px-4 py-2 rounded-lg">
                  <span className="text-sm font-semibold">Peak Rates: 3-5x Normal</span>
                </div>
                <div className="bg-white/20 px-4 py-2 rounded-lg">
                  <span className="text-sm font-semibold">Book 6+ Months Ahead</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Enforcement Warning */}
        <div className="mt-12 bg-[#882629]/5 border-l-4 border-[#882629] p-6 rounded-lg">
          <div className="flex items-start gap-4">
            <i className="ri-alert-line text-2xl text-[#882629] flex-shrink-0 mt-1"></i>
            <div>
              <h4 className="font-semibold text-[#505A5A] mb-2">Proactive Enforcement in 2026</h4>
              <p className="text-[#505A5A]/70 leading-relaxed">
                Calgary conducts proactive audits of online listings. Fines start at $500+ for missing license numbers in advertisements. Ensure your license is visible on all platforms to avoid penalties.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
